import { useState, useEffect, useCallback, useRef } from "react";

interface CardCarouselProps {
  images: string[];
  title: string;
  /** Durée entre chaque slide en ms (défaut : 3000) */
  interval?: number;
  /** Ratio d'aspect de l'image (défaut : "4/3") */
  aspectRatio?: string;
  className?: string;
}

/**
 * Mini-carrousel pour les cartes de la page d'accueil.
 * - Défilement automatique
 * - Effet Ken Burns (zoom lent) sur l'image active
 * - Indicateurs de position cliquables
 * - Pause au survol
 */
export function CardCarousel({
  images,
  title,
  interval = 3000,
  aspectRatio = "4/3",
  className = "",
}: CardCarouselProps) {
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const next = useCallback(() => {
    setCurrent((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(next, interval);
  }, [next, interval]);

  useEffect(() => {
    if (!isPaused && images.length > 1) {
      startTimer();
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPaused, startTimer, images.length]);

  const goTo = (index: number) => {
    setCurrent(index);
    startTimer(); // reset timer on manual navigation
  };

  return (
    <div
      className={`relative overflow-hidden rounded-t-2xl gallery-zoom-container ${className}`}
      style={{ aspectRatio }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Overlay zoom au survol */}
      <div className="absolute inset-0 z-20 pointer-events-none transition-opacity duration-300 opacity-0 group-hover:opacity-100" style={{ background: 'rgba(0,0,0,0.08)' }} />
      {/* Images avec effet Ken Burns */}
      {images.map((img, idx) => (
        <div
          key={idx}
          className="absolute inset-0 transition-opacity duration-700"
          style={{ opacity: idx === current ? 1 : 0 }}
        >
          <img
            src={img}
            alt={`${title} - réalisation ${idx + 1}`}
            width={600}
            height={800}
            decoding="async"
            className="w-full h-full object-cover ecoline-gallery-img"
            style={{
              transform: idx === current
                ? "scale(1.12)"
                : "scale(1)",
              transition: idx === current
                ? `transform ${interval}ms ease-out`
                : "transform 0.7s ease-out",
            }}
            loading="lazy"
          />
        </div>
      ))}

      {/* Gradient bas pour lisibilité */}
      <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />

      {/* Indicateurs de position */}
      {images.length > 1 && (
        <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5 z-10">
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={(e) => {
                e.stopPropagation();
                goTo(idx);
              }}
              className="rounded-full transition-all duration-300 hover:scale-125"
              style={{
                width: idx === current ? "20px" : "6px",
                height: "6px",
                backgroundColor: idx === current ? "white" : "rgba(255,255,255,0.5)",
              }}
              aria-label={`Image ${idx + 1}`}
            />
          ))}
        </div>
      )}

      {/* Badge "Réalisations" */}
      <div className="absolute top-3 left-3 z-10">
        <span className="bg-black/50 backdrop-blur-sm text-white text-xs font-medium px-2.5 py-1 rounded-full">
          {current + 1} / {images.length}
        </span>
      </div>
    </div>
  );
}
